/**
 * Auth API service.
 * Handles authentication-related API calls (public endpoints).
 */

import { BASE_URL } from '../../../shared/lib/api';

export interface BetaEmailCheckResponse {
  approved: boolean;
  used: boolean;
}

export interface SignupEmailCheckResponse {
  can_signup: boolean;
  reason: 'approved' | 'email_already_used' | 'not_on_beta_list' | 'beta_email_already_used' | 'invalid_email_format' | 'error';
  error?: string;
}

export interface BetaRequestResponse {
  success: boolean;
  id: string;
  duplicate?: boolean;
}

export interface CreateProfileRequest {
  email: string;
  username?: string;
  first_name?: string;
  last_name?: string;
}

/**
 * Check if username is available (public endpoint).
 */
export async function checkUsernameAvailable(username: string): Promise<boolean> {
  const response = await fetch(`${BASE_URL}/auth/check-username/${encodeURIComponent(username)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to check username');
  }

  const data = await response.json();
  return data.available;
}

/**
 * Check if email is available (public endpoint).
 * Now includes beta email information.
 */
export async function checkEmailAvailable(email: string): Promise<{ available: boolean; on_beta_list: boolean; beta_used: boolean }> {
  const response = await fetch(`${BASE_URL}/auth/check-email/${encodeURIComponent(email)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to check email');
  }

  const data = await response.json();
  return {
    available: data.available,
    on_beta_list: data.on_beta_list || false,
    beta_used: data.beta_used || false,
  };
}

/**
 * Check if email is on the beta list and if it's been used.
 */
export async function checkBetaEmail(email: string): Promise<BetaEmailCheckResponse> {
  const response = await fetch(`${BASE_URL}/auth/check-beta-email/${encodeURIComponent(email)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to check beta email');
  }

  return await response.json();
}

/**
 * Submit a beta access request.
 */
export async function submitBetaRequest(data: { 
  first_name: string; 
  last_name: string; 
  email: string;
  investment_platforms?: string;
  referral_source?: string;
}): Promise<BetaRequestResponse> {
  const response = await fetch(`${BASE_URL}/auth/beta-request`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to submit beta request');
  }

  return await response.json();
}

/**
 * Comprehensive check for signup email validation.
 * Checks: email not already used AND email in beta_emails AND not used.
 */
export async function checkSignupEmail(email: string): Promise<SignupEmailCheckResponse> {
  const response = await fetch(`${BASE_URL}/auth/check-signup-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to check signup email');
  }

  return await response.json();
}

/**
 * Create user profile after signup (called after email confirmation).
 * Requires authentication token.
 */
export async function createProfile(token: string, data: CreateProfileRequest): Promise<any> {
  const response = await fetch(`${BASE_URL}/auth/create-profile`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create profile');
  }

  return await response.json();
}

